import isEqual from "fast-deep-equal/es6";
import { useEffect, useRef } from "react";
import { InstantSearch, useInstantSearch } from "react-instantsearch";
import { toIndexUiState } from "../chat/filters";
import { Filters } from "../filter/Facet";
import { useWorkspaces } from "../state";
import { fromIndexUiState } from "./criteria";
import { useWorkspaceSearch } from "./search";

const future = { preserveSharedStateOnUnmount: true };

// Zet de criteria van een aangeklikt filter om naar InstantSearch-uiState.
// De ref houdt setIndexUiState stabiel: een inline arrow als effect-dependency
// geeft een oneindige render-loop.
const ApplyCriteria = () => {
  const { setIndexUiState } = useInstantSearch();
  const setStateRef = useRef(setIndexUiState);
  useEffect(() => {
    setStateRef.current = setIndexUiState;
  });

  const criteria = useWorkspaceSearch((s) => s.criteria);
  const appliedAt = useWorkspaceSearch((s) => s.appliedAt);

  // Ook op `appliedAt`: dezelfde chip nog eens aanklikken laat `criteria`
  // ongemoeid (zelfde object), maar moet wél opnieuw zoeken — anders blijven de
  // zoekparameters leeg die `apply()` net heeft gewist.
  useEffect(() => {
    setStateRef.current(criteria ? toIndexUiState(criteria) : {});
  }, [criteria, appliedAt]);

  return null;
};

// Wacht tot de facetten even stilstaan: elke klik in een lijst zou anders een
// PATCH zijn, en vlak na het openen staat er heel even nog een lege stand.
const SAVE_DELAY_MS = 800;

// Een verkenning bewaart zijn laatste filter, ook als dat uit de chat kwam: wat
// er op de kaart staat, is wat de verkenning onthoudt.
const SaveCriteria = ({ explorationId }: { explorationId: number }) => {
  const { indexUiState } = useInstantSearch();
  const criteria = fromIndexUiState(indexUiState as any);
  const key = JSON.stringify(criteria);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      // Alleen terwijl het filter van de verkenning actief is: na een wissel
      // van werkruimte (`clear()`) staan de facetten even leeg, en dat is niet
      // het filter van de gebruiker.
      if (!useWorkspaceSearch.getState().active) return;
      const { workspaces, saveCriteria } = useWorkspaces.getState();
      const stored = workspaces?.find((w) => w.id === explorationId)?.criteria ?? {};
      const current = JSON.parse(key);
      if (!isEqual(stored, current)) void saveCriteria(explorationId, current);
    }, SAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [key, explorationId]);

  return null;
};

// Headless: geen `routing` (dat zou de workspace-URL overschrijven). De widgets
// van /search staan er altijd, verborgen — anders heeft setIndexUiState geen
// effect zodra een zichtbaar facetvak dichtgeklapt (en dus unmount) is.
export const WorkspaceSearch = ({
  explorationId,
  children,
}: {
  explorationId?: number;
  children: React.ReactNode;
}) => {
  const client = useWorkspaceSearch((s) => s.client);
  return (
    <InstantSearch indexName="reference" searchClient={client} future={future}>
      <div className="hidden">
        <Filters />
      </div>
      <ApplyCriteria />
      {explorationId && <SaveCriteria explorationId={explorationId} />}
      {children}
    </InstantSearch>
  );
};
